import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, QueryList, ViewChildren} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {catchError, filter, first, map, observeOn, share, shareReplay, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {asyncScheduler, combineLatest, EMPTY, forkJoin, fromEvent, merge, NEVER, Subject} from 'rxjs';

import {SnackBarService} from '../../../../core-services/snack-bar/snack-bar.service';
import {MatTableDataSource} from '@angular/material/table';
import {AdManagementService} from '../../ad-management.service';
import {addHours, startOfDay} from 'date-fns';
import {MerchantApiService} from '../../../../core/services/api/merchant-api.service';
import { DashboardApiService} from '../../../../core/services/api/dashboard-api.service';
import {merchantUrlValidator} from '../../../../core/pure-utils/validators';
import {
  AffiliateExclusiveOption, CreateMerchantAdRequest,
  MerchantAd,
  TagExclusiveOption
} from '../../../../core/dtos/api';
import {TagApiService} from '../../../../core/services/api/tag-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import {BannerAd} from '../../ad-management';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TitleCasePipe} from '@angular/common';
import {PreviewAdDialogComponent} from '../preview-ad-dialog/preview-ad-dialog.component';
import {triggerHotJarGa} from '../../../../core/pure-utils/analytics';
import {hourTimeOptions} from '../../../../core/core-variables';

@Component({
    templateUrl: './create-ad-dialog.component.html',
    styleUrls: ['./create-ad-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
            provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
        }],
    standalone: false
})
export class CreateAdDialogComponent implements OnDestroy {
  @ViewChildren('dropZone') dropZone: QueryList<ElementRef<HTMLDivElement>>;

  readonly triggerAnalytics = triggerHotJarGa;
  readonly exclusiveAffiliateLimit = 100;
  readonly exclusiveTagLimit = 10;
  readonly hourTimeOptions = hourTimeOptions;
  readonly allAffiliatesOption = <AffiliateExclusiveOption> { affiliate_name: 'All Affiliates', filterType: 'affiliates' };

  exclusiveFilterPreviousInput = this.allAffiliatesOption;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  now = new Date();
  dataSource = new MatTableDataSource<BannerAd>();
  previousEndTime = 0;
  saveClick$ = new Subject();
  disableSaveButton = false;

  ngDestroy$ = new Subject();
  merchantUrls$ = this.adManagementService.merchantWebsites$.pipe(
    takeUntil(this.ngDestroy$),
    shareReplay(1)
  );
  adType = this.fb.control('text');
  textAdFormControls = {
    textAdLinkText: this.fb.control('', [Validators.required, Validators.maxLength(255)]),
    textAdName: this.fb.control('', [Validators.required, Validators.maxLength(255)]),
    destinationUrl: this.fb.control('', [], [merchantUrlValidator(this.merchantUrls$)])
  };
  bannerAdFormControls = {
    banners: this.fb.array([], [Validators.required, Validators.maxLength(255)]),
    destinationUrl: this.fb.control('', [], [merchantUrlValidator(this.merchantUrls$)])
  };
  textAdFormGroup = this.fb.group(this.textAdFormControls);
  bannerAdFormGroup = this.fb.group(this.bannerAdFormControls);
  adFormGroup: UntypedFormGroup; // Used for step validation.

  adScheduleFormControls = {
    startDate: this.fb.control(startOfDay(this.now), Validators.required),
    startTime: this.fb.control(0),
    endDate: this.fb.control(null),
    endTime: this.fb.control({value: -1, disabled: true})
  };
  adScheduleFormGroup = this.fb.group(this.adScheduleFormControls);

  affiliateDesignationFormControls = {
    exclusiveFilter: this.fb.control(this.allAffiliatesOption)
  };
  affiliateDesignationFormGroup = this.fb.group(this.affiliateDesignationFormControls);

  adType$ = this.adType.valueChanges.pipe(startWith('text'));
  exclusiveFilter$ =
    this.affiliateDesignationFormControls.exclusiveFilter.valueChanges.pipe(startWith<string>(this.affiliateDesignationFormControls.exclusiveFilter.value));
  // In order to validate a step we need the form group to validate based on the view (text ad or banner ads).
  adFormGroup$Sub = this.adType$.pipe(
    tap(x => {
      if (x === 'text') {
        this.adFormGroup = this.fb.group(this.textAdFormControls);
      }
      if (x === 'banner') {
        this.adFormGroup = this.fb.group(this.bannerAdFormControls);
      }
    })
  ).subscribe();

  endTime$Sub = this.adScheduleFormControls.endDate.valueChanges
    .subscribe(endDate => {
      if (endDate == null) {
        this.previousEndTime = this.adScheduleFormControls.endTime.value;
        this.adScheduleFormControls.endTime.setValue(-1);
      } else if (this.adScheduleFormControls.endTime.value === -1) {
        this.adScheduleFormControls.endTime.setValue(this.previousEndTime);
      }
    });

  merchantId$ = this.dashboardApiService.currentAccount$.pipe(
    first(),
    map(currentAccount => currentAccount.entity_id),
    share()
  );

  usersAffiliates$ = this.dashboardApiService.usersAffiliates$;
  affiliateFilteredOptions$ = combineLatest([this.exclusiveFilter$, this.usersAffiliates$]).pipe(
    map(([affiliateFilter, affiliateOptions]) => {
      if (affiliateFilter === '' || typeof affiliateFilter !== 'string') {
        return affiliateOptions;
      }

      let lowerInput = affiliateFilter.toLowerCase();
      return affiliateOptions.filter(x => x.affiliate_name.toLowerCase().indexOf(lowerInput) !== -1 ||
        x.affiliate_website_url_protocol_stripped.indexOf(lowerInput) !== -1 ||
        String(x.classic_affiliate_id).indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  affiliateFilteredOptionsLimited$ = this.affiliateFilteredOptions$.pipe(
    map(x => x.length > this.exclusiveAffiliateLimit ? x.slice(0, this.exclusiveAffiliateLimit) : x));

  usersTagGroups$ = this.tagApiService.usersTagGroups$;
  userTagFilteredOptions$ = combineLatest([this.exclusiveFilter$, this.usersTagGroups$]).pipe(
    map(([exclusiveFilter, tagOptions]) => {
      if (exclusiveFilter === '' || typeof exclusiveFilter !== 'string') {
        return tagOptions;
      }

      let lowerInput = exclusiveFilter.toLowerCase();
      return tagOptions.filter(x => x.tag_name.toLowerCase().indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  userTagFilteredOptionsLimited$ = this.userTagFilteredOptions$.pipe(
    map(x => x.length > this.exclusiveTagLimit ? x.slice(0, this.exclusiveTagLimit) : x));

  exclusiveFilterDisplayWith = (value: AffiliateExclusiveOption | TagExclusiveOption): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value.filterType === 'affiliates') {
      return value.affiliate_name.trim();
    }
    if (value.filterType === 'tag_groups') {
      return this.titleCasePipe.transform(`${value.tag_name.trim()} Tag Group`);
    }
    return value;
  };
  autoSelectOnOpened = (formControl: UntypedFormControl) => formControl.setValue('');
  exclusiveFilterAutoCompleteOnSelected = ($event: MatAutocompleteSelectedEvent, filteredExclusiveInput: HTMLInputElement) => {
    this.exclusiveFilterPreviousInput = $event.option.value;
    filteredExclusiveInput.blur();
  };
  exclusiveFilterAutoCompleteOnClosed = () => {
    if (typeof this.affiliateDesignationFormControls.exclusiveFilter.value === 'string') {
      this.affiliateDesignationFormControls.exclusiveFilter.setValue(this.exclusiveFilterPreviousInput);
    }
  };

  merchantDefaultWebsite$Sub = combineLatest([
    this.merchantUrls$.pipe(
      first(), map(websites => websites.filter(website => website.is_default)), shareReplay({bufferSize: 1, refCount: true})),
    this.adType.valueChanges.pipe(startWith('text'))]
  ).subscribe(([website, adType]) => {
    if (this.adType.value === 'text') {
      this.textAdFormControls.destinationUrl.setValue(website[0].merchant_website_url);
      this.textAdFormControls.destinationUrl.markAsTouched();
      this.textAdFormControls.destinationUrl.updateValueAndValidity();
    } else {
      this.bannerAdFormControls.destinationUrl.setValue(website[0].merchant_website_url);
      this.bannerAdFormControls.destinationUrl.markAsTouched();
      this.bannerAdFormControls.destinationUrl.updateValueAndValidity();
    }
  });

  fileInput$ = new Subject<Event>();
  fileInputFiles$ = this.fileInput$.pipe(map(event => {
    let fileInput = (<HTMLInputElement> event.currentTarget);
    let files = fileInput?.files;
    if (files == null) {
      return <File[]> <unknown> NEVER;
    }
    let filesArray = Array.from(files);
    // reset fileInput so change event is triggered if same file name is uploaded consecutively.
    fileInput.value = '';
    return filesArray;
  }));
  dragEvents$ = merge(fromEvent(document, 'dragenter', {capture: true}), fromEvent(document, 'dragover', {capture: true}),
    fromEvent(document, 'drop', {capture: true})).pipe(
    tap(event => {
      event.stopPropagation();
      event.preventDefault();
    }),
    share());
  hoveringInDropZone$ = this.dragEvents$.pipe(map((event: DragEvent) => {
    let inZone = event.composedPath().some(x => x === this.dropZone.first.nativeElement || x === this.dropZone.last.nativeElement) &&
      event.type !== 'drop' && event.dataTransfer.types.includes('Files');

    event.dataTransfer.dropEffect = inZone ? 'copy' : 'none';
    return inZone;
  }));
  droppedFiles$ = this.dragEvents$.pipe(
    filter(event => event.type === 'drop'),
    map((event: DragEvent) => Array.from(event.dataTransfer.files)));
  selectedFiles$ = merge(this.fileInputFiles$, this.droppedFiles$).pipe(
    filter(files => {
      let maxFileSize = (1024 * 300); // 300 kbs.
      for (let file of files) {
        let lowerFilename = file.name.toLowerCase();
        if (!(lowerFilename.endsWith('.jpg') || lowerFilename.endsWith('.jpeg') || lowerFilename.endsWith('.gif') || lowerFilename.endsWith('.png'))) {
          this.snackBarService.openErrorSnackBar('The image must be a .jpg, .jpeg, .gif or .png type.');
          return false;
        }
        if (file.size > maxFileSize) {
          this.snackBarService.openErrorSnackBar('Max file size is 300 kbs.');
          return false;
        }
      }
      return true;
    })
  );
  selectedFiles$Sub = this.selectedFiles$.pipe(
    tap(files => {
      const maxNumberBannerAds = 20;
      let allowableNumberBannerAds = (maxNumberBannerAds - this.bannerAdFormControls.banners.length) > 0
        ? (maxNumberBannerAds - this.bannerAdFormControls.banners.length) : 0;
      let allowableFiles = files.slice(0, allowableNumberBannerAds);
      let bannerAdControlGroups = allowableFiles.map(file => this.fb.group({
        bannerAdImageFile: this.fb.control(file, Validators.required),
        bannerAdName: this.fb.control('', [Validators.required, Validators.maxLength(255)])
      }));

      let bannerAds = allowableFiles.map(file => ({
        bannerAdImageFile: file,
        bannerAdName: ''
      }));

      bannerAdControlGroups.forEach(controlGroup => this.bannerAdFormControls.banners.push(controlGroup));
      this.dataSource.data = [...this.dataSource.data, ...bannerAds];
      if (files.length > allowableNumberBannerAds) {
        this.snackBarService.openErrorSnackBar('You can only upload 20 images at a time.');
      }
    })
  ).subscribe();

  saveClick$Sub = this.saveClick$.pipe(
    switchMap(() => this.merchantId$),
    tap(x => this.disableSaveButton = true),
    switchMap((merchantId) => {
      let merchantAdRequestObjects = this.getMerchantAdRequestObjects();
      let merchantAdRequests = merchantAdRequestObjects.map(adRequestObject => this.merchantApiService.createMerchantAds(merchantId, adRequestObject));
      return forkJoin(merchantAdRequests).pipe(
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.openErrorSnackBar(error.error.error);
          this.dialogRef.close(true);
          return EMPTY;
        })
      );
    })
  ).subscribe((merchantAds) => {
    this.dialogRef.close(true);
    this.snackBarService.openSnackBar('Ad(s) have been saved.');
    });

  private getMerchantAdRequestObjects(): CreateMerchantAdRequest[] {
    let merchantAds = [];
    let startDateTime = addHours(this.adScheduleFormControls.startDate.value, this.adScheduleFormControls.startTime.value);
    let endDateTime = this.adScheduleFormControls.endDate.value
      ? addHours(this.adScheduleFormControls.endDate.value, this.adScheduleFormControls.endTime.value) : null;
    if (this.adType.value === 'text') {
      merchantAds = [{
        adType: 'text',
        adTitle: this.textAdFormControls.textAdName.value,
        linkText: this.textAdFormControls.textAdLinkText.value,
        destinationUrl: this.textAdFormControls.destinationUrl.value,
        startDate: startDateTime,
        endDate: endDateTime
      }];
    } else if (this.adType.value === 'banner') {
      merchantAds = this.bannerAdFormControls.banners.controls.map(banner => ({
        adType: 'banner',
        adTitle: banner.value.bannerAdName,
        imageFile: banner.value.bannerAdImageFile,
        destinationUrl: this.bannerAdFormControls.destinationUrl.value,
        startDate: startDateTime,
        endDate: endDateTime
      }));
    }

    let exclusiveType = this.affiliateDesignationFormControls.exclusiveFilter.value.filterType;
    let exclusiveUuid = exclusiveType === 'affiliates' ? this.affiliateDesignationFormControls.exclusiveFilter.value.affiliate_id
      : this.affiliateDesignationFormControls.exclusiveFilter.value.tag_id;
    if (exclusiveType === 'tag_groups'
      || (exclusiveType === 'affiliates' && this.affiliateDesignationFormControls.exclusiveFilter.value?.affiliate_name !== 'All Affiliates')) {
      merchantAds = merchantAds.map(merchantAd => ({...merchantAd, restrictedEntityGroupName: exclusiveType, restrictedEntityId: exclusiveUuid}));
    }
    return merchantAds;
  }

  displayedColumns: string[] = ['bannerAdImageFile', 'bannerAdName', 'deleteBannerAd'];

  constructor(public dialogRef: MatDialogRef<CreateAdDialogComponent>, private fb: UntypedFormBuilder, private snackBarService: SnackBarService,
              private adManagementService: AdManagementService, private merchantApiService: MerchantApiService, private dashboardApiService: DashboardApiService,
              private tagApiService: TagApiService, private changeDetectorRef: ChangeDetectorRef,
              private titleCasePipe: TitleCasePipe, private dialog: MatDialog) {
  }

  onDeleteBannerRowClick(adPosition: number) {
    this.dataSource.data.splice(adPosition, 1);
    this.dataSource.data = [...this.dataSource.data];
    this.bannerAdFormControls.banners.removeAt(adPosition);
  }

  ngOnDestroy(): void {
    this.adFormGroup$Sub.unsubscribe();
    this.selectedFiles$Sub.unsubscribe();
    this.endTime$Sub.unsubscribe();
    this.saveClick$Sub.unsubscribe();
    this.ngDestroy$.next(null);
    this.ngDestroy$.complete();
  }

  onPreviewClick() {
    this.dialog.open(PreviewAdDialogComponent, {
      data: {
        ad_type: 'text',
        link_text: this.textAdFormControls.textAdLinkText.value,
        destination_url: this.textAdFormControls.destinationUrl.value
      } as MerchantAd,
      maxHeight: '90vh',
      minWidth: '300px'
    });
  }
}
